import {createSelector} from 'reselect';

import {roundScore} from '@homeaway/react-review-components';
import get from 'lodash/get';

import {getListing, getListingPage, getTranslations, getTravelerFeatures} from './store';
import {labelMaker} from '../../server/utils/labelMaker';
import {getJsReviews} from './reviews';
import {getAverageNightly, getConditionalInstantBook, getTotals} from './priceDetails';
import {getTripDataDates} from './tripDates';
import {getTripNights} from './tripNights';
import {getIsMobile, getBrandName} from './system';
import {getFamilyFriendlyBadge, getInstallmentsBadge} from './badges';
import getUnitId from '../utils/getUnitId';
import {
    getAveragePrice,
    getAverageRating,
    getBookingType,
    getGeography,
    getHostType,
    getListingHeadline,
    getListingIds,
    getMultiUnit
} from './listing';
import {getPremierPartnerBadge} from './badges';
import {areBothDatesValid} from '../utils/dateHelper';

/* eslint-disable complexity */
const getSuperlative = createSelector(
    [getAverageRating, getTranslations],
    (averageRating, translations) => {
        const RATING_EXCEPTIONAL = 5.0;
        const RATING_WONDERFUL = 4.8;
        const RATING_EXCELLENT = 4.3;
        const RATING_VERYGOOD = 4.0;
        const RATING_GOOD = 3.7;
        const scaled = roundScore(averageRating);
        let superlative = '';

        // Determine superlative (e.g. "Wonderful!")
        if (scaled === RATING_EXCEPTIONAL) {
            superlative = translations.superlativeExceptional;
        } else if (scaled >= RATING_WONDERFUL) {
            superlative = translations.superlativeWonderful;
        } else if (scaled >= RATING_EXCELLENT) {
            superlative = translations.superlativeExcellent;
        } else if (scaled >= RATING_VERYGOOD) {
            superlative = translations.superlativeVeryGood;
        } else if (scaled >= RATING_GOOD) {
            superlative = translations.superlativeGood;
        }

        return superlative;
    }
);
/* eslint-enable complexity */

const getBookingAssistance = createSelector(
    [getBookingType, getTranslations, getIsMobile, getBrandName],
    (bookingType, translations, isMobile, brandName) => {
        const {payPerBooking, onlineBookable} = bookingType;

        const {bookingAssistNum, bookingAssistPPSNum, bookingAssistBrand} = translations;

        let number = bookingAssistNum;

        if (!payPerBooking && onlineBookable) {
            number = bookingAssistPPSNum;
        }

        const htmlMobile = `<a class="booking-phone__a" href="tel:${number}"> ${number}</a>`;
        const htmlDesktop = `<strong>${number}</strong>`;
        const html = isMobile ? htmlMobile : htmlDesktop;
        const labelMakerParams = {
            number: html,
            brand: brandName
        };
        const transformed = labelMaker(bookingAssistBrand, labelMakerParams);

        return transformed;
    }
);

/* eslint-disable complexity */
const getPrice = createSelector(
    [
        getAveragePrice,
        getAverageNightly,
        getTotals,
        getTripNights,
        getTranslations,
        getTravelerFeatures,
        getListing,
        getListingPage
    ],
    (
        averagePrice = {},
        averageNightly,
        totals,
        tripNights,
        translations,
        travelerFeatures,
        listing,
        listingPage
    ) => {
        let amount = averagePrice.localized || '';
        let label = translations.averageNightly;
        const pricingKey = listingPage.pricingKey;
        const priceSummaryEventParams = {
            unituuid: getUnitId(listing.unitApiUrl, false),
            eventstartdate: pricingKey.get('arrival'),
            eventenddate: pricingKey.get('departure'),
            eventnumadults: (pricingKey.get('adults') || 0).toString(),
            eventnumchildren: (pricingKey.get('children') || 0).toString(),
            eventpets: (pricingKey.get('pets') || false).toString(),
            eventcurrency: pricingKey.get('currency')
        };

        if (!amount) {
            // Some listings will not have a price due to it being unbookable, or disabled.
            label = '';
        }

        const validDates = areBothDatesValid(
            pricingKey.get('arrival'),
            pricingKey.get('departure')
        );

        if (validDates) {
            if (travelerFeatures.showTotal) {
                const totalAmount = totals.totalWithoutDeposits;

                if (totalAmount) {
                    amount = totalAmount;
                    label = labelMaker(translations.nightTotal, tripNights);
                }
            } else {
                const nightlyAmount = travelerFeatures.includeTaxesAndFees
                    ? averageNightly.averageNightlyTotalTrip
                    : averageNightly.averageNightlyTotalRent;

                if (nightlyAmount) {
                    amount = nightlyAmount;
                    label = translations.perNight;
                }
            }
        }

        // merge the final price and description into the EDAP event payload
        priceSummaryEventParams.stramount = amount;
        priceSummaryEventParams.perioddescription = label;

        return {
            amount,
            label,
            priceSummaryEventParams
        };
    }
);
/* eslint-enable complexity */

const getLocation = createSelector(
    [getGeography],
    (geography) => {
        return get(geography, 'description') || null;
    }
);

const getReviewCount = createSelector(
    [getJsReviews],
    (reviewsData) => {
        return reviewsData.reviewCount;
    }
);

const getListingSummary = createSelector(
    [
        getBookingAssistance,
        getPrice,
        getLocation,
        getListingPage,
        getPremierPartnerBadge,
        getTranslations,
        getSuperlative,
        getReviewCount,
        getConditionalInstantBook,
        getTripDataDates,
        getAverageRating,
        getListingHeadline,
        getBookingType,
        getHostType,
        getListingIds,
        getMultiUnit,
        getFamilyFriendlyBadge,
        getInstallmentsBadge
    ],
    (
        bookingAssistance,
        price,
        location,
        listingPage,
        premierPartnerBadge,
        translations,
        superlative,
        reviewCount,
        conditionalInstantBook,
        tripDataDates,
        averageRating,
        listingHeadline,
        bookingType,
        hostType,
        listingIds,
        multiUnit,
        familyFriendlyBadge,
        installmentsBadge
    ) => {
        let propertyNum = labelMaker(translations.propertyNum, {propertynumber: ''});

        const headline =
            multiUnit.isMultiUnit && multiUnit.unitName
                ? `${listingHeadline} - ${multiUnit.unitName}`
                : listingHeadline;
        const instantBookTooltip = hostType.owner
            ? translations.instantBookTooltipOwner
            : translations.instantBookTooltipManager;

        const labels = {
            reviews: labelMaker(translations.reviewCount, {REVIEWCOUNT: reviewCount}),
            averageNightly: translations.averageNightly,
            instantBook: translations.instantBook,
            instantBookTooltip,
            instantConfirmation: translations.instantConfirmation,
            perNight: translations.perNight,
            map: translations.navMap,
            priceLabel: price.label,
            propertyNum,
            bookingAssist: bookingAssistance,
            unitNum: translations.unitNum
        };

        return {
            superlative,
            headline,
            location,
            premierPartner: premierPartnerBadge,
            propertyOlb: bookingType.onlineBookable,
            reviewCountLabel: labels.reviews,
            rating: averageRating,
            ratingRounded: roundScore(averageRating),
            loadingRate: listingPage.loadingRate,
            instantBookable: bookingType.instantBook || conditionalInstantBook,
            multiUnit: multiUnit.isMultiUnit,
            listingId: listingIds.listingId,
            propertyId: listingIds.propertyId,
            unitNumber: multiUnit.unitNumber,
            reviewCount,
            price: price.amount,
            priceSummaryEventParams: price.priceSummaryEventParams,
            installmentsBadge,
            familyFriendlyBadge,
            labels
        };
    }
);

export default getListingSummary;
export {
    getListingSummary,
    getSuperlative,
    getLocation,
    getBookingAssistance,
    getPrice,
    getReviewCount
};
